import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../Styles";
import { Button, Overlay } from "../../Components";
import { ButtonTypes } from "../../Definitions";

/* eslint-disable */
export enum MainContentPaddingTypes {
  primary="primary",
  secondary = "secondary"
}
/* eslint-enable */

interface NewModal_Props {
  modalState: boolean
  header: string
  subHeader?: string
  headerContent?: JSX.Element
  mainContent?: JSX.Element
  secondaryContent?: JSX.Element
  footerContent?: JSX.Element
  footerMessage?: string
  topRightContent?: JSX.Element
  topLeftContent?: JSX.Element
  predefinedButtons?:{
    onClose?: () => void
    onGoBack?: () => void
    onNext?: () => void
    onPrev?: () => void
  }
  overrides?: {
    mainContentPadding?: MainContentPaddingTypes
    mainContentScroll?: boolean
    mainContentVisibleOverflow?: boolean
    headerContentHeight?: string
    backgroundColor?: string
    overlayColor?: string
  }
} 

export default function NewModal({modalState, header, subHeader, headerContent, mainContent, secondaryContent, footerContent, footerMessage, topRightContent, topLeftContent, overrides, predefinedButtons}:NewModal_Props) {
  const {t} = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(modalState)
  const [isVisible, setIsVisible] = useState<boolean>(modalState);
  const mobileXs = useMediaQuery(`(max-width: 690px)`);

  useEffect(() => {
    if (modalState) {
      setIsVisible(true);
      setTimeout(() => setIsModalOpen(true), 100);
    } else {
      setIsModalOpen(false);
      setTimeout(() => {
        setIsVisible(false);
      }, 100);
    }
  }, [modalState]);

  // Main Content Padding
  let _padding;
  switch (overrides&&overrides.mainContentPadding) {
    case MainContentPaddingTypes.secondary:
      _padding = (
        footerContent && mobileXs 
        ? "0px 10px" 
        : footerContent 
          ? "0px 40px" 
          : mobileXs
            ? "0px 10px 50px 10px"  
            : "0px 40px 50px 40px"
      )
      break;
    case MainContentPaddingTypes.primary:
      _padding = "20px"
    break;
    default:
      _padding = "0px";
    break;
  }

  // #region | Predefined Buttons
  const CloseBtn = ( predefinedButtons&&predefinedButtons.onClose ?
    <Button 
      content={""}
      startEnhancer={glyphs.Close({})}
      onClick={() => (predefinedButtons&&predefinedButtons.onClose) ? predefinedButtons.onClose() : undefined} 
      buttonType={ButtonTypes.textButton}
      overrides={{
        startEnhancerStyles: {margin: "0px -5px 0px 0px"}
      }}
    />
  : <></> );

  const GoBackBtn = ( predefinedButtons&&predefinedButtons.onGoBack ?
    <Button 
      content={t("buttons.goBack", {defaultValue: "go back"})}
      startEnhancer={glyphs.Arrow({rotate: 180})}
      onClick={predefinedButtons.onGoBack} 
      buttonType={ButtonTypes.textButton}
      overrides={{
        baseButton: {whiteSpace: "nowrap"}
      }}
    />
  : <></> );

  const NextBtn = ( predefinedButtons&&predefinedButtons.onNext ?
    <Button 
      content={t("buttons.next", {defaultValue: "next"})}
      buttonType={ButtonTypes.textButton}
      endEnhancer={glyphs.Arrow({})}
      onClick={() => (predefinedButtons&&predefinedButtons.onNext) ? predefinedButtons.onNext() : undefined} 
    />
  : <></> );

  const PrevBtn = ( predefinedButtons&&predefinedButtons.onPrev ?
    <Button 
      content={t("buttons.prev", {defaultValue: "prev"})}
      buttonType={ButtonTypes.textButton}
      startEnhancer={glyphs.Arrow({rotate: 180})}
      onClick={() => (predefinedButtons&&predefinedButtons.onPrev) ? predefinedButtons.onPrev() : undefined} 
    />
  : <></> );
  // #endregion

    const _headerContentHeight = overrides&&overrides.headerContentHeight ? overrides.headerContentHeight : "50px"

  return ( <>
    {isVisible && createPortal ( <>
      <Overlay 
        overrides={{
          color: overrides&&overrides.overlayColor ? overrides.overlayColor : undefined,
          genericStyle: {opacity: isModalOpen? "1" : "0", transition: "opacity 0.1s ease-out"},
          zIndex: isModalOpen ? 999999999 : 0
        }}
        content={
          <div style={{
            backgroundColor: colors.blueDarkPrimary, 
            width: "fit-content",
            maxWidth: "90%",
            minWidth: "340px",
            height: "fit-content",
            maxHeight: "90%", 
            borderRadius: "10px", 
            border: "5px solid "+colors.blueDarkPrimary,
            position: "relative",
            boxSizing: "border-box",
            display: "grid",
            gridTemplateColumns: "100px 1fr",
            gridTemplateRows: `20px 100px ${_headerContentHeight} 1fr fit-content(100%)`,
            zIndex: isModalOpen ? "9999999999" : "0",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            opacity: isModalOpen? "1" : "0",
            transition: "opacity 0.1s ease-out"
          }}>

            {/* Top right btn area */}
            <div style={{gridArea: "1/1/2/2", display: "flex", justifyContent: "start", alignItems: "start", padding: "5px 0px 0px 10px", zIndex: "2"}}>
              {topRightContent}
              {GoBackBtn}
            </div>

            {/* Top left btn area */}
            <div style={{gridArea: "1/2/2/3", display: "flex", justifyContent: "end", alignItems: "start", padding: "5px 10px 0px 0px", zIndex: "2"}}>
              {topLeftContent}
              {CloseBtn}
            </div>

            {/* Header */}
            <div style={{gridArea: "2/1/3/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0px 20px"}}>
              <p style={{...fonts.header, ...fonts.fontSize0, ...fonts.textShadow, color: colors.greyLightTertiary, textAlign: "center"}}>{header}</p>
              <p style={{...fonts.header, ...fonts.fontSize2, ...fonts.textShadow, color: colors.greyLightTertiary, textAlign: "center"}}>{subHeader}</p>
            </div>

            {/* Header Content */}
            {(headerContent||(predefinedButtons&&predefinedButtons.onNext)||(predefinedButtons&&predefinedButtons.onPrev)) ?
              <div style={{gridArea: "3/1/4/3", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "20px"}}>
                {PrevBtn}
                {headerContent}
                {!headerContent&&((predefinedButtons&&predefinedButtons.onNext)||(predefinedButtons&&predefinedButtons.onPrev)) ?
                  <div style={{height: "40%", width: "2px", backgroundColor: colors.blueDarkSecondary}}/>
                : <></>}
                {NextBtn}
              </div>
            : <></> }

            {/* Content */}
            <div style={{
              gridArea: 
              (headerContent || (predefinedButtons && (predefinedButtons.onNext || predefinedButtons.onPrev))) && footerContent 
              ? "4/1/5/3" 
              : (headerContent || (predefinedButtons && (predefinedButtons.onNext || predefinedButtons.onPrev))) 
                ? "4/1/6/3"  
                : footerContent
                  ? "3/1/5/3"  
                  : "3/1/6/3", 
              boxSizing: "border-box", 
              display: "flex", 
              // height: "fit-content",
              alignItems: "center", 
              justifyContent: "center", 
              borderRadius: "0px 0px 8px 8px",
              backgroundColor: overrides&&overrides.backgroundColor? overrides.backgroundColor : colors.blueDarkPrimary,
              overflow: overrides&&overrides.mainContentVisibleOverflow === true ? "visible" : "hidden"
            }}>
              <div style={{
                height: "100%", 
                boxSizing: "border-box", 
                width: "100%",
                padding: _padding, 
                overflow: overrides&&overrides.mainContentScroll ? "auto" : "visible",
                scrollbarColor: colors.greyTertiary+colors.transparent,
                scrollbarWidth: "thin",
              }}>
                {secondaryContent ? 
                  <div style={{margin: "20px 0px", padding: "10px", maxHeight: "40vh", boxSizing: "border-box", borderTop: "2px solid "+colors.blueDarkSecondary, borderBottom: "2px solid "+colors.blueDarkSecondary, overflowY: "auto", scrollbarColor: colors.blueDarkSecondary+colors.transparent, scrollbarWidth: "thin"}}>
                  {/* // <div style={{margin: "20px 0px", padding: "10px", maxHeight: "40vh", boxSizing: "border-box", borderTop: "2px solid "+colors.blueDarkSecondary, borderBottom: "2px solid "+colors.blueDarkSecondary, overflowY: "scroll", scrollbarColor: colors.blueDarkSecondary+colors.transparent, scrollbarWidth: "thin", scrollbarGutter: "stable"}}> */}
                    { secondaryContent }
                  </div> 
                : <></>}
                {mainContent}
              </div>
            </div>

            {/* Footer */}
            {footerContent ? 
              <div style={{gridArea: "5/1/6/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginBottom: "20px"}}>
                <div style={{height: "20px", ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, transition: "opacity 0.2s ease", opacity: footerMessage ? "1" : "0"}}>{footerMessage}</div>
                {footerContent}
              </div> 
            : <></>}

          </div>
        }
      />
    </>, document.body)}
  </>)
}